import { useEffect, useMemo, useState } from "react";
import {
  DialogTitle,
  DialogDescription,
  DialogContent,
  V2Dialog,
  MultiStepForm,
  VisuallyHidden,
  V2Loader,
} from "web-design-system";
import {
  ClientOnboardingContext,
  PersonalInfo,
  AddressInfo,
  OtherInfo,
  BankDetails,
  ContactDetails,
  NomineeDetails,
  OnboardingProgress,
  TaxResidency,
  MinorDetails,
  Guardian,
  GuardianProfile,
  Documents,
  MinorOnboardingContext,
  isNewGuardian,
  buildOnboardingProgressSteps,
} from "web-core/components";
import { useRouter, useSearch } from "@tanstack/react-router";
import { useClient, useInvestorInfo, useMinorGuardian } from "creso-core/hooks";
import { Loader2 } from "lucide-react";
import { Route } from "@/routes/__root";
import ErrorBoundary from "@core/components/error-boundary";
import omit from "lodash/omit";
import { getOnboardingSteps, isMinor, isNRI } from "creso-core/utils";
import { NotifyClient } from "./notify-client";

function ClientOnboardingFormLoader({
  initialClientId,
  close,
  initialStep,
}: {
  initialClientId?: number;
  close: () => void;
  initialStep?: number;
}) {
  const [clientId, setClientId] = useState<number | undefined>(initialClientId);

  useEffect(() => {
    setClientId(initialClientId);
  }, [initialClientId]);

  const { refetch } = useClient(clientId ?? 0, false);

  const { data, isLoading: isLoadingInfo } = useInvestorInfo(
    clientId as number
  );
  const nri = isNRI(data?.client ?? undefined);
  const minor = isMinor(data?.client);
  // Read here so a new guardian can add the Guardian profile step
  const { guardian, isLoading: isLoadingGuardian } = useMinorGuardian(
    data?.client
  );
  const isLoading = isLoadingInfo || isLoadingGuardian;
  const newGuardian = minor && isNewGuardian(guardian);
  const steps = getOnboardingSteps(data?.client, { newGuardian });

  const defaultStep = useMemo(() => {
    const incompleteStepIndex = steps.findIndex((s) => !s.completed(data));
    return incompleteStepIndex + 1;
  }, [data, steps]);

  const step = initialStep ?? defaultStep;

  if (defaultStep === 0 || isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  return (
    <MinorOnboardingContext.Provider value={{ guardian }}>
      <ClientOnboardingContext.Provider
        value={{
          client: data?.client ?? null,
          close,
          updateClientId: setClientId,
          refetchClient: refetch,
        }}
      >
        <div className="flex overflow-hidden flex-row rounded-lg">
          <div className="flex flex-1 rounded-r-lg">
            <V2Loader className="flex-row" isLoading={step === -1}>
              <MultiStepForm
                defaultStep={step - 1}
                initialData={{}}
                progressBar={({ currentStep }) => (
                  <div className="flex rounded-l-lg bg-linear-to-b from-secondary to-muted">
                    <OnboardingProgress
                      name={data?.client?.name}
                      currentStep={currentStep}
                      steps={buildOnboardingProgressSteps(data, {
                        currentStep,
                        steps,
                      })}
                    />
                  </div>
                )}
              >
                {/* Steps are indexed by position, so NRI-only ones are spliced in, not rendered as null */}
                {minor
                  ? [
                      <MinorDetails key="personal-details" />,
                      <Guardian key="guardian" />,
                      ...(newGuardian
                        ? [<GuardianProfile key="guardian-profile" />]
                        : []),
                      <AddressInfo key="address" minor />,
                      <BankDetails key="bank-details" minor />,
                      <ContactDetails key="declaration" minor />,
                      <Documents key="documents" />,
                      <NotifyClient key="notify-client" />,
                    ]
                  : [
                      <PersonalInfo key="personal-details" />,
                      <AddressInfo key="address" overseas={nri} />,
                      ...(nri ? [<TaxResidency key="tax-residency" />] : []),
                      <BankDetails key="bank-details" nri={nri} />,
                      <OtherInfo key="financial-profile" />,
                      <NomineeDetails key="nominee" />,
                      <ContactDetails key="declaration" />,
                      <NotifyClient key="notify-client" />,
                    ]}
              </MultiStepForm>
            </V2Loader>
          </div>
        </div>
      </ClientOnboardingContext.Provider>
    </MinorOnboardingContext.Provider>
  );
}

export function ClientOnboardingDialog() {
  const router = useRouter();
  const search = useSearch({
    from: Route.id,
  });

  const handleOpenChange = (open: boolean) => {
    if (open) return;
    if (router.history.canGoBack()) {
      router.history.back();
    } else {
      router.navigate({
        to: ".",
        search: (prev) => omit(prev, ["modal", "client_id", "step"]),
        replace: true,
      });
    }
  };

  const { client_id: clientId, step: initialStep } = search;

  return (
    <V2Dialog defaultOpen onOpenChange={handleOpenChange}>
      <VisuallyHidden>
        <DialogTitle>Client Onboarding</DialogTitle>
        <DialogDescription id="add-client">Client Onboarding</DialogDescription>
      </VisuallyHidden>
      <DialogContent
        onInteractOutside={() => {
          router.invalidate();
        }}
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="h-160 max-w-170 rounded-lg p-0"
      >
        <ErrorBoundary>
          <ClientOnboardingFormLoader
            close={() => handleOpenChange(false)}
            initialClientId={clientId}
            initialStep={initialStep}
          />
        </ErrorBoundary>
      </DialogContent>
    </V2Dialog>
  );
}
